/**
 * BranchHierarchyPicker · v3.9.11 / v101.6.0
 * Selector jerárquico Sucursal → Bodega materiales → Bodega devolución → Zonas.
 * Fuente de verdad:
 *   - branches: tabla `branches` (8 sucursales oficiales)
 *   - bodegas por sucursal: GET /inventory/warehouses?branch={nomenclatura}
 *   - zonas por sucursal: pasa `zones` completo, filtrado interno por branch_id
 *
 * Props:
 *   branch_id, warehouse_location_id, return_warehouse_location_id, zone_ids
 *   onChange({ branch_id, warehouse_location_id, return_warehouse_location_id, zone_ids })
 *   branches, zones  → catálogos precargados
 *   testIdPrefix
 */
import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { MapPin, Warehouse, Building2, Loader2, RotateCcw } from "lucide-react";

export default function BranchHierarchyPicker({
  branch_id,
  warehouse_location_id,
  return_warehouse_location_id,
  zone_ids,
  onChange,
  branches = [],
  zones = [],
  testIdPrefix = "bhp",
  // v101.8.0 · Las zonas se administran SOLO en «Gestión Geográfica»: aquí se muestran sin editar
  zonesReadOnly = false,
}) {
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWh, setLoadingWh] = useState(false);

  const selectedBranch = branches.find((b) => b.id === branch_id);

  // Cargar bodegas cuando cambia la sucursal
  useEffect(() => {
    if (!selectedBranch) { setWarehouses([]); return; }
    const nom = selectedBranch.nomenclatura || selectedBranch.code;
    if (!nom) return;
    setLoadingWh(true);
    api.get(`/inventory/warehouses?branch=${encodeURIComponent(nom)}`)
      .then(({ data }) => setWarehouses(data.items || []))
      .catch(() => setWarehouses([]))
      .finally(() => setLoadingWh(false));
  }, [branch_id, selectedBranch]);

  // Zonas filtradas por sucursal
  const zonesOfBranch = useMemo(() => {
    if (!branch_id) return [];
    return zones.filter((z) => z.branch_id === branch_id);
  }, [zones, branch_id]);

  const emit = (patch) => {
    onChange({
      branch_id,
      warehouse_location_id,
      return_warehouse_location_id: return_warehouse_location_id ?? "",
      zone_ids,
      ...patch,
    });
  };

  const handleBranchChange = (bid) => {
    // v101.6.0 · Reset ambas bodegas y zonas al cambiar sucursal
    emit({ branch_id: bid, warehouse_location_id: "", return_warehouse_location_id: "",
           ...(zonesReadOnly ? {} : { zone_ids: [] }) });
  };

  const toggleZone = (zid) => {
    const next = (zone_ids || []).includes(zid)
      ? (zone_ids || []).filter((x) => x !== zid)
      : [...(zone_ids || []), zid];
    emit({ zone_ids: next });
  };

  return (
    <div className="space-y-3">
      {/* Nivel 1: Sucursal */}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 flex items-center gap-1">
          <Building2 size={11}/> Paso 1 · Sucursal
        </label>
        <select
          value={branch_id || ""}
          onChange={(e) => handleBranchChange(e.target.value)}
          className="w-full input"
          data-testid={`${testIdPrefix}-branch`}
        >
          <option value="">— Sin sucursal —</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.nomenclatura || b.code} · {b.name}</option>
          ))}
        </select>
      </div>

      {/* Nivel 2: Bodega de materiales (asignada) */}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 flex items-center gap-1">
          <Warehouse size={11}/> Paso 2 · Bodega de materiales
          {loadingWh && <Loader2 size={10} className="animate-spin ml-1"/>}
        </label>
        {/* v101.10.0 · Buscador de bodega */}
        <WhSearchSelect
          value={warehouse_location_id}
          onChange={(v) => emit({ warehouse_location_id: v })}
          warehouses={warehouses}
          disabled={!branch_id}
          emptyLabel={branch_id
            ? (warehouses.length === 0 ? "— Sin bodegas en esta sucursal —" : "— Elegir bodega de materiales —")
            : "Selecciona primero una sucursal"}
          testId={`${testIdPrefix}-warehouse`}
        />
        <p className="text-[10px] text-gray-400 mt-0.5">Materiales nuevos / stock de salida del técnico.</p>
      </div>

      {/* Nivel 3: Bodega de devoluciones · v101.6.0 */}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 flex items-center gap-1">
          <RotateCcw size={11}/> Paso 3 · Bodega de devoluciones
          {loadingWh && <Loader2 size={10} className="animate-spin ml-1"/>}
        </label>
        {/* v101.10.0 · Buscador de bodega */}
        <WhSearchSelect
          value={return_warehouse_location_id}
          onChange={(v) => emit({ return_warehouse_location_id: v })}
          warehouses={warehouses}
          disabled={!branch_id}
          emptyLabel={branch_id
            ? (warehouses.length === 0 ? "— Sin bodegas en esta sucursal —" : "— Elegir bodega de devoluciones —")
            : "Selecciona primero una sucursal"}
          testId={`${testIdPrefix}-return-warehouse`}
        />
        <p className="text-[10px] text-gray-400 mt-0.5">
          Destino de equipos/materiales que el técnico retira del cliente (módulo Devoluciones).
        </p>
      </div>

      {/* Nivel 4: Zonas (multi, filtradas por sucursal) */}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 flex items-center gap-1">
          <MapPin size={11}/> Paso 4 · Zonas de cobertura ({(zone_ids || []).length})
        </label>
        {zonesReadOnly ? (
          <div className="space-y-1" data-testid={`${testIdPrefix}-zones-readonly`}>
            <div className="flex flex-wrap gap-1.5 border rounded p-2 bg-gray-50 min-h-[34px]">
              {(zone_ids || []).length === 0 ? (
                <span className="text-[11px] text-gray-400 italic">Sin zonas asignadas</span>
              ) : (zone_ids || []).map((zid) => {
                const z = zones.find((x) => String(x.id) === String(zid));
                return (
                  <span key={zid} className="text-[10px] px-2 py-1 rounded border font-mono bg-blue-50 text-blue-800 border-blue-200">
                    <MapPin size={8} className="inline mr-0.5"/>{z ? (z.code || z.name) : String(zid).slice(0, 8)}
                  </span>
                );
              })}
            </div>
            <div className="text-[11px] text-gray-500">
              Las zonas se asignan en <a href="/admin/zonas" className="underline">Gestión Geográfica</a> → zona → Técnicos.
            </div>
          </div>
        ) : !branch_id ? (
          <div className="text-[11px] text-gray-400 italic p-2 border border-dashed rounded">
            Selecciona primero una sucursal para ver sus zonas.
          </div>
        ) : zonesOfBranch.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic p-2 border border-dashed rounded">
            Esta sucursal no tiene zonas configuradas. Ve a <a href="/admin/zonas" className="underline">Zonas</a> para crearlas.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto border rounded p-2 bg-white">
            {zonesOfBranch.map((z) => {
              const checked = (zone_ids || []).includes(z.id);
              return (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => toggleZone(z.id)}
                  className={`text-[10px] px-2 py-1 rounded border font-mono transition ${
                    checked
                      ? "bg-blue-100 text-blue-800 border-blue-300"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid={`${testIdPrefix}-zone-${z.id}`}
                >
                  <MapPin size={8} className="inline mr-0.5"/>
                  {z.code || z.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


/** v101.10.0 · Selector de bodega con buscador (nombre o ruta completa, sin importar tildes/mayúsculas). */
const _norm = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function WhSearchSelect({ value, onChange, warehouses = [], disabled, emptyLabel, testId }) {
  const [q, setQ] = useState("");
  const terms = _norm(q).split(/\s+/).filter(Boolean);
  const filtered = terms.length
    ? warehouses.filter((w) => {
        const hay = _norm(`${w.location_name || ""} ${w.location_complete_name || w.complete_name || ""} ${w.location_id}`);
        return terms.every((t) => hay.includes(t));
      })
    : warehouses;
  // La bodega ya elegida siempre visible aunque no coincida con la búsqueda
  const selected = warehouses.find((w) => String(w.location_id) === String(value || ""));
  const list = selected && !filtered.includes(selected) ? [selected, ...filtered] : filtered;
  return (
    <div className="space-y-1">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        disabled={disabled}
        placeholder="Buscar bodega (nombre, técnico, código)…"
        className="w-full input text-xs disabled:bg-gray-100 disabled:cursor-not-allowed"
        data-testid={`${testId}-search`}
      />
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
        disabled={disabled}
        className="w-full input disabled:bg-gray-100 disabled:cursor-not-allowed"
        data-testid={testId}
        size={terms.length && list.length > 1 ? Math.min(list.length + 1, 7) : undefined}
      >
        <option value="">{emptyLabel}</option>
        {list.map((w) => (
          <option key={w.location_id} value={w.location_id}>
            {w.kind === "central" ? "★ " : ""}{w.location_name}
          </option>
        ))}
      </select>
      {terms.length > 0 && (
        <p className="text-[10px] text-gray-500" data-testid={`${testId}-count`}>
          {filtered.length} de {warehouses.length} bodegas coinciden
          {filtered.length === 0 ? " — revise la sucursal o el texto" : ""}
        </p>
      )}
    </div>
  );
}
